import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Seq, Set } from 'immutable';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TcsCustomPermission } from 'src/app/shared/authorization/permissions';
import { CompanyContextService } from 'src/app/shared/company-context.service';
import { EntityType, UserRole } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import {
  CommunicationCenterProps,
  CommunicationCenterRecord,
  selectAllCommunicationCenterEntities,
} from 'src/store/generated/communication-center-record.generated';
import { filterNull } from 'src/util/rxjs/operators/filter-null';
import { UserViewModel } from 'src/models/person-view-model';
import { AccessibleItemProps } from 'src/store/generated/accessible-item-record.generated';
import { undefinedOrSwitchMap } from 'src/util/rxjs';
import { PermissionsService } from '../amg-authorization/permissions.service';
import { UserService } from '../tcs-identity/user.service';
import { AccessibleItemAuthorizationService } from './accessible-item-authorization.service';
import { OpsReaderAuthorizationService } from './ops-reader-authorization.service';

@Injectable({ providedIn: 'root' })
export class CommCenterAuthorizationService {
  constructor(
    private _user: UserService,
    private _accessibleItems: AccessibleItemAuthorizationService,
    private _permissions: PermissionsService,
    private _store: Store<AppState>,
    private _companyContextService: CompanyContextService,
    private _opsReaderAuthz: OpsReaderAuthorizationService,
  ) {}

  public isAuthorizedForCommCenter(
    commCenterId: string,
    includeReadonlyCommCenters: boolean = true,
  ): Observable<boolean> {
    return this.getAuthorizedCommCenters(undefined, includeReadonlyCommCenters).pipe(
      map((_) => _.find((cc) => cc.id === commCenterId) != null),
    );
  }

  public isAuthorizedForCompany(
    companyId: string,
    includeReadonlyCommCenters: boolean = true,
  ): Observable<boolean> {
    return this._isAuthorizedForCompany(companyId, undefined, includeReadonlyCommCenters);
  }

  public getAuthorizedCommCentersForCurrentCompany(
    includeReadonlyCommCenters: boolean = true,
  ): Observable<Seq.Indexed<CommunicationCenterProps>> {
    return this._companyContextService.company$.pipe(
      filterNull(),
      switchMap((company) => this.getAuthorizedCommCenters(company.id, includeReadonlyCommCenters)),
    );
  }

  public getAuthorizedCommCenters(
    companyId?: string,
    includeReadonlyCommCenters: boolean = true,
  ): Observable<Seq.Indexed<CommunicationCenterProps>> {
    return this._user.user$.pipe(
      undefinedOrSwitchMap((user) => {
        const readonlyCommCenters$ = includeReadonlyCommCenters
          ? this._opsReaderAuthz.getReadonlyCommCenters(companyId)
          : of(Seq.Indexed<CommunicationCenterRecord>());
        const commCenters$ = this._store
          .select(selectAllCommunicationCenterEntities)
          .pipe(
            map((commCenters) =>
              commCenters.filter((c) => companyId == null || companyId === c.companyId).valueSeq(),
            ),
          );
        const accessibleItems$ = this._accessibleItems.getAccessibleItems();
        return combineLatest([commCenters$, accessibleItems$, readonlyCommCenters$]).pipe(
          map(([commCenters, accessibleItems, readonlyCommCenters]) => {
            if (this._checkUserAllAccessiblePermissions(user, accessibleItems, companyId)) {
              return commCenters.concat(readonlyCommCenters);
            }
            return commCenters
              .filter((cc) => {
                return this._checkAuthorizedForCompany(cc.companyId, user, accessibleItems, cc.id);
              })
              .concat(readonlyCommCenters);
          }),
        );
      }),
      map((ccs) => ccs ?? Seq.Indexed<CommunicationCenterProps>()),
    );
  }

  private _isAuthorizedForCompany(
    companyId: string,
    commCenterId?: string,
    includeReadonlyCommCenters: boolean = true,
  ) {
    const commCenters$ = this._store
      .select(selectAllCommunicationCenterEntities)
      .pipe(
        map((commCenters) =>
          commCenters.filter((c) => companyId == null || companyId === c.companyId).valueSeq(),
        ),
      );
    const readonlyAuthorized$ = includeReadonlyCommCenters
      ? this._opsReaderAuthz.isReadonlyForCompany(companyId)
      : of(false);
    const accessibleItems$ = this._accessibleItems.getAccessibleItems();
    return combineLatest([
      this._user.user$,
      accessibleItems$,
      commCenters$,
      readonlyAuthorized$,
    ]).pipe(
      map(([user, accessibleItems, commCenters, readonlyAuthorized]) => {
        if (commCenters.count() > 0) {
          const authorizedForCompany = commCenters.filter((cc) => {
            return this._checkAuthorizedForCompany(
              cc.companyId,
              user,
              accessibleItems,
              cc.id,
              readonlyAuthorized,
            );
          });
          return authorizedForCompany.count() > 0;
        } else {
          return this._checkAuthorizedForCompany(
            companyId,
            user,
            accessibleItems,
            commCenterId,
            readonlyAuthorized,
          );
        }
      }),
    );
  }

  private _checkAuthorizedForCompany(
    companyId: string,
    user: UserViewModel | undefined,
    accessibleItems: AccessibleItemProps[],
    commCenterId?: string,
    isReadonlyAuthorized?: boolean,
  ) {
    if (user == null) {
      return false;
    }

    if (this._checkUserAllAccessiblePermissions(user, accessibleItems, companyId)) {
      return true;
    }

    const commCenterAi = accessibleItems.find(
      (ai) => ai.entityId === commCenterId && ai.type === EntityType.CommunicationCenter,
    );
    if (this._hasScopedPermission(commCenterAi)) {
      // User has been granted access to this specific comm center
      return true;
    }

    return isReadonlyAuthorized ?? false;
  }

  private _checkUserAllAccessiblePermissions(
    user: UserViewModel,
    accessibleItems: AccessibleItemProps[],
    companyId?: string,
  ) {
    if (
      this._hasScopedPermission(user) ||
      (user.allCommunicationCentersAccessible === true && user.allCompaniesAccessible === true)
    ) {
      // User has global permission to access all comm centers
      return true;
    }
    if (companyId != null) {
      const companyAi = accessibleItems.find(
        (ai) => ai.entityId === companyId && ai.type === EntityType.Company,
      );
      if (
        this._hasScopedPermission(companyAi) ||
        (companyId === user.companyId && user.allCommunicationCentersAccessible === true) ||
        (companyAi != null && user.allCommunicationCentersAccessible === true)
      ) {
        // User has been granted access to this comm center because
        // they have accesss to all comm centers in this company
        return true;
      }
    }
    return false;
  }

  private _hasScopedPermission(
    scope:
      | {
          userRoles: Set<UserRole> | null;
        }
      | undefined,
  ) {
    if (scope == null) {
      return false;
    }
    const { userRoles } = scope;
    if (userRoles == null) {
      return false;
    }
    return this._permissions.hasPermission(
      userRoles.toArray(),
      TcsCustomPermission.AccessCommCentersWithinScope,
    );
  }
}
